import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';

import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import GenericSelect from '../../components/ui/GenericSelect';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { isOk } from '../../utils/apiResult';
import { useHrAnalyticsApi, resolveDefaultScope, RECRUITING_METRIC_CODES } from './config';

// İŞE ALIM RAPORU — ATS ailesinin sekiz HA metriğini TEK tabloda okur.
//
// ═══════════ YENİ UÇ YOK ═══════════
// Sayfa mevcut okuma ucundan (GET /v1/ha/metrics) beslenir; sekiz kod TEK istekte istenir
// (uç `codes` CSV'sini zaten destekliyor ve her kodu bir kez işler — bkz. GetMetricsAsync'in
// Distinct'i). Metrik gezgini (HrAnalyticsMetricDetailPage) bu metrikleri TEK TEK zaten
// gösteriyordu; burada eksik olan, ailenin BİR ARADA ve dışa aktarılabilir hâlde okunmasıydı.
//
// ═══════════ LOKASYON KIRILIMI YOK — ÜRÜN GERÇEĞİ ═══════════
// Kapsam seçicisi kataloğun `supportedScopes` alanından gelir, SABİT KODLANMAZ. İşe alım
// ailesinin tamamı katalogda `NoLocation` taşır: AtsRequisition.Location serbest METİNDİR
// (Guid FK değil) ve bir lokasyon hücresinin ScopeId'sine çevrilemez (bkz. HaMetricCatalog.cs).
// Seçicide Lokasyon'un ÇIKMAMASI bu yüzden bir eksiklik değil, doğru cevaptır.
//
// ═══════════ BASTIRMA SUNUCUDA BİTER ═══════════
// İstemci bastırılmış bir hücrenin ham değerini HİÇ ALMAZ: okuma yolu Value/Numerator/
// Denominator/N alanlarını DTO'ya hiç yazmaz, yalnız `suppressedReason` gelir (bkz.
// HaMetricsDataAccess.MapCell). Sayfa o alanı GÖSTERİR, yeniden karar VERMEZ — burada ikinci
// bir eşik mantığı olsaydı ekran ile sunucu bir gün ayrışırdı.
//
// ═══════════ DIŞA AKTARIM DA AYNI GÖVDEDEN ═══════════
// "Excel'e Aktar" GET /v1/ha/metrics/export'u AYNI parametrelerle çağırır; sunucu tarafında
// o uç da GetMetricsAsync'i çağırıp yalnız onun bastırılmış hücrelerini yazar. Yani dosya
// ekranda görünenin birebir aynısıdır — ham veri Excel'den sızamaz.

const GRAIN = 'month';

// Dönem penceresi: HA anlık görüntüleri AYLIK yazılır, bu yüzden seçim de AY cinsindendir.
// DataTable'ın metin kutulu sütun süzgeci burada BİLEREK kullanılmıyor (disableFilters):
// "Dönem" bir TARİH sütunudur ve tarihi serbest metin olarak süzdürmek bu depoda kapatılmış
// bir kusur sınıfıdır. Pencere seçimi sunucuya `from` olarak gider, yani süzme veriyi
// çekmeden ÖNCE olur.
const WINDOW_OPTIONS = [6, 12, 24];
const DEFAULT_WINDOW = 12;

// Süre birimleri düz SAYI okunur ("14,2 gün"); geri kalan her şey ORAN olduğu için yüzde
// okunur. Sekiz metriğin birimi katalogdan gelir (HaUnit) — burada birim TABLOSU tutulmaz,
// yalnız "süre mi değil mi" ayrımı yapılır.
const DURATION_UNITS = new Set(['day', 'month', 'hour', 'minute']);

const Controls = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;

  > div {
    min-width: 200px;
  }
`;

// Tek kaydırma DataTable satırlarıdır: sayfa gövdesi taşmaz, kaydırma tablonun kendi
// gövdesindedir (tableBodyMaxHeight). Bu sarmalayıcı bu yüzden min-height: 0 taşır.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const isoMonthsAgo = (months) => {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months + 1, 1));
  return d.toISOString().slice(0, 10);
};

export default function HrAnalyticsRecruitingPage() {
  const { t, i18n } = useTranslation();
  const { getCatalog, getSettings, getMetrics, exportMetrics } = useHrAnalyticsApi();

  const [metaByCode, setMetaByCode] = useState({});
  const [supportedScopes, setSupportedScopes] = useState([]);
  const [scope, setScope] = useState(null);
  const [windowMonths, setWindowMonths] = useState(DEFAULT_WINDOW);
  const [results, setResults] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Adım 1 — katalog + ayarlar. Desteklenen kapsamlar ailenin ORTAK kesişiminden alınır:
  // sekiz metrik de aynı `NoLocation` kümesini taşıyor, ama bunu varsaymak yerine kesişim
  // hesaplanır — bir metriğin kapsamı yarın daralırsa seçici SESSİZCE yanlış vaat etmesin.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getCatalog(), getSettings()]).then(([catalogRes, settingsRes]) => {
      if (cancelled) return;
      const catalog = isOk(catalogRes) ? (catalogRes.data?.data ?? []) : [];
      const meta = {};
      catalog.forEach((m) => { if (RECRUITING_METRIC_CODES.includes(m.code)) meta[m.code] = m; });

      const present = RECRUITING_METRIC_CODES.filter((c) => meta[c]);
      const scopes = present.length === 0 ? [] : present
        .map((c) => meta[c].supportedScopes ?? [])
        .reduce((acc, list) => acc.filter((s) => list.includes(s)));

      const order = settingsRes?.data?.data?.primaryScopeOrder ?? [];
      setMetaByCode(meta);
      setSupportedScopes(scopes);
      setScope(resolveDefaultScope(order, scopes));
    });
    return () => { cancelled = true; };
  }, [getCatalog, getSettings]);

  const queryParams = useMemo(() => ({
    codes: RECRUITING_METRIC_CODES.join(','),
    scope,
    grain: GRAIN,
    from: isoMonthsAgo(windowMonths),
  }), [scope, windowMonths]);

  // Adım 2 — hücreleri çek. api.js GET'lerde REJECT ETMEZ: gerçek sinyal res.data.status'tur
  // (utils/apiResult.js). "Yüklenemedi" ile "bu dönemde veri yok" ayrı görünmek ZORUNDA —
  // biri hata bandı, diğeri tablodaki durum sütunudur.
  useEffect(() => {
    if (!scope) return undefined;
    let cancelled = false;
    setFetching(true);
    getMetrics(queryParams)
      .then((res) => {
        if (cancelled) return;
        const ok = isOk(res);
        setLoadFailed(!ok);
        setResults(ok ? (res.data?.data ?? []) : []);
      })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [scope, queryParams, getMetrics]);

  const periodFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'long', timeZone: 'UTC' }),
    [i18n.language]
  );
  const percentFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 }),
    [i18n.language]
  );
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language]
  );
  const countFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }),
    [i18n.language]
  );

  // Metrik sonuçları → düz tablo satırları. ÜÇ durum AYRI satır anlamı taşır ve tek bir
  // "boş" hücreye indirgenmeleri kullanıcıyı yanıltırdı (dosya tarafında da aynı ayrım var,
  // bkz. HaMetricsExporter.Build):
  //   • kilitli metrik → tek satır + kilit gerekçesi
  //   • açık ama hücresiz → tek satır + "veri yok"
  //   • hücreli → hücre başına satır (bastırılmışsa değerler boş, durum gizlilik metni)
  const rows = useMemo(() => {
    const out = [];
    RECRUITING_METRIC_CODES.forEach((code) => {
      const result = results.find((r) => r.code === code);
      const name = t(`hrAnalytics.metricNames.${code}`, { defaultValue: code });
      if (!result) return;

      if (result.state !== 'available') {
        out.push({
          id: code,
          metric: name,
          scopeName: '',
          period: '',
          value: '',
          basis: '',
          status: t(`responseErrors.${result.reasonKey}`, { defaultValue: result.reasonKey }),
        });
        return;
      }

      const cells = result.cells ?? [];
      if (cells.length === 0) {
        out.push({
          id: code,
          metric: name,
          scopeName: '',
          period: '',
          value: '',
          basis: '',
          status: t('hrAnalytics.recruiting.noPeriodData'),
        });
        return;
      }

      const unit = metaByCode[code]?.unit || 'none';
      const isDuration = DURATION_UNITS.has(unit);

      cells.forEach((cell, index) => {
        const suppressed = cell.suppressedReason != null;
        const hasValue = !suppressed && cell.value !== null && cell.value !== undefined;
        const hasNumerator = !suppressed && cell.numerator !== null && cell.numerator !== undefined;
        out.push({
          id: `${code}|${cell.periodStart}|${cell.scopeId ?? 'tenant'}|${index}`,
          metric: name,
          scopeName: cell.scopeId == null
            ? t('hrAnalytics.recruiting.companyWide')
            : (cell.scopeName || (suppressed ? '' : t('hrAnalytics.recruiting.unknownScope'))),
          period: periodFormatter.format(new Date(cell.periodStart)),
          value: hasValue
            ? (isDuration
              ? t(`hrAnalytics.units.${unit}.count`, {
                n: numberFormatter.format(cell.value), defaultValue: numberFormatter.format(cell.value),
              })
              : percentFormatter.format(cell.value))
            : '',
          basis: hasNumerator
            ? t(`hrAnalytics.units.${unit}.ratio`, {
              n: countFormatter.format(cell.numerator),
              d: cell.denominator === null || cell.denominator === undefined
                ? '' : countFormatter.format(cell.denominator),
              defaultValue: '',
            })
            : '',
          status: suppressed
            ? t(`responseErrors.${cell.suppressedReason}`, { defaultValue: cell.suppressedReason })
            : '',
        });
      });
    });
    return out;
  }, [results, metaByCode, t, periodFormatter, percentFormatter, numberFormatter, countFormatter]);

  const columns = useMemo(() => ([
    { name: 'metric', label: t('hrAnalytics.recruiting.col.metric'), options: { filter: false, sort: false } },
    { name: 'scopeName', label: t('hrAnalytics.recruiting.col.scope'), options: { filter: false, sort: false } },
    { name: 'period', label: t('hrAnalytics.recruiting.col.period'), options: { filter: false, sort: false } },
    { name: 'value', label: t('hrAnalytics.recruiting.col.value'), options: { filter: false, sort: false } },
    { name: 'basis', label: t('hrAnalytics.recruiting.col.basis'), options: { filter: false, sort: false } },
    { name: 'status', label: t('hrAnalytics.recruiting.col.status'), options: { filter: false, sort: false } },
  ]), [t]);

  const scopeOptions = useMemo(
    () => supportedScopes.map((s) => ({ id: s, name: t(`hrAnalytics.scopes.${s}`) })),
    [supportedScopes, t]
  );

  const windowOptions = useMemo(
    // İnterpolasyon değişkeni `n` — `count` DEĞİL: i18next `count`'u çoğul ANAHTARI olarak
    // yorumlar (lastMonths_one / _other) ve beş dilin çoğul kuralları farklıdır; burada tek
    // bir şablon yeterli olduğu için o makineye hiç girilmez (units.*.ratio ile AYNI kural).
    () => WINDOW_OPTIONS.map((m) => ({ id: m, name: t('hrAnalytics.recruiting.lastMonths', { n: m }) })),
    [t]
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await exportMetrics(queryParams);
      // Başarısız GET'te interceptor headers'sız EMPTY_RESPONSE ({status:false}) resolve eder
      // ve hatayı zaten toast'lamıştır — indirme yolunu sessizce terk et (TalentReportsPage'in
      // AYNI koruması; yoksa res.headers[...] TypeError → unhandled rejection).
      if (!res?.headers || res.data?.status === false) return;
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
      a.download = match ? match[1].replaceAll('"', '') : 'ik-analitigi-ise-alim.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [exportMetrics, queryParams]);

  return (
    <Container>
      {loadFailed && <LoadErrorBanner />}

      <Controls>
        <GenericSelect
          label={t('hrAnalytics.recruiting.scopeLabel')}
          options={scopeOptions}
          value={scope}
          onChange={setScope}
        />
        <GenericSelect
          label={t('hrAnalytics.recruiting.windowLabel')}
          options={windowOptions}
          value={windowMonths}
          onChange={setWindowMonths}
        />
      </Controls>

      <DataTable
        title={t('hrAnalytics.recruiting.pageTitle')}
        fetching={fetching}
        columns={columns}
        data={rows}
        serverSide={false}
        pageSize={20}
        disableFilters
        tableBodyMaxHeight="calc(100vh - 340px)"
        toolbarActions={(
          <Button
            type="button"
            $variant="outline"
            data-role="ha-recruiting-export"
            onClick={handleExport}
            disabled={exporting || rows.length === 0}
          >
            <Download size={14} style={{ marginRight: 6 }} />
            {exporting ? t('common.loading') : t('hrAnalytics.recruiting.export')}
          </Button>
        )}
      />
    </Container>
  );
}
